import { cn } from "@/lib/utils";
import React, { useMemo, useRef, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Input } from "./input";

type ComboboxOption = {
  render: () => React.ReactNode;
  searchableText: string;
  value: string;
};

type ComboboxProps = {
  groups: {
    groupName?: string;
    options: ComboboxOption[];
  }[];
  selected: string[];
  onSelectionChange: (value: string, selected: boolean) => void;
  children: React.ReactNode;
  searchPlaceholder?: string;
  align?: React.ComponentProps<typeof DropdownMenuContent>["align"];
  side?: React.ComponentProps<typeof DropdownMenuContent>["side"];
  disabled?: boolean;
};

export function Combobox({
  groups,
  selected,
  onSelectionChange,
  children,
  searchPlaceholder,
  align,
  side,
  disabled = false,
}: ComboboxProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState("");
  const selectedSet = new Set(selected);
  const availableGroups = useMemo(() => {
    const alreadySelected = new Set();
    const availableGroups = [];
    for (const group of groups) {
      const available = [];
      for (const option of group.options) {
        if (
          selectedSet.has(option.value) &&
          !alreadySelected.has(option.value)
        ) {
          alreadySelected.add(option.value);
        }
        if (
          option.searchableText.toLowerCase().includes(filter.toLowerCase())
        ) {
          available.push(option);
        }
      }
      availableGroups.push({
        groupName: group.groupName,
        options: available,
      });
    }
    return availableGroups;
  }, [groups, filter]);

  const handleDropdownOpenChange = (open: boolean) => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      });
    }
  };

  return (
    <DropdownMenu onOpenChange={handleDropdownOpenChange}>
      <DropdownMenuTrigger disabled={disabled} asChild>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        className="flex flex-col gap-2"
      >
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder={searchPlaceholder || "Search..."}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            ref={inputRef}
          />
        </div>
        <div className="overflow-x-hidden overflow-y-auto">
          {availableGroups.map((group, index) => (
            <React.Fragment key={group.groupName + "-" + index}>
              {index > 0 && <DropdownMenuSeparator />}
              {group.groupName && (
                <DropdownMenuLabel className="text-muted-foreground text-xs">
                  {group.groupName}
                </DropdownMenuLabel>
              )}
              <DropdownMenuGroup>
                {group.options.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    disabled={selectedSet.has(option.value)}
                    className={cn("w-full", {
                      "text-muted-foreground": selectedSet.has(option.value),
                      "cursor-default": selectedSet.has(option.value),
                      "cursor-pointer": !selectedSet.has(option.value),
                    })}
                    onClick={() => onSelectionChange(option.value, true)}
                  >
                    {option.render()}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </React.Fragment>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
